import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "@/api/client";
import { api } from "@/api/endpoints";
import { Composer } from "@/components/chat/Composer";
import { GuestBanner } from "@/components/chat/GuestBanner";
import { AssistantMessage, UserMessage } from "@/components/chat/Messages";
import { QueryLoading } from "@/components/chat/QueryLoading";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { Wordmark } from "@/components/common/Wordmark";
import { newGuestMessageId, useGuest } from "@/store/guest";

export function ChatGuest() {
  const navigate = useNavigate();
  const location = useLocation();
  const messages = useGuest((s) => s.messages);
  const thinking = useGuest((s) => s.thinking);
  const append = useGuest((s) => s.append);
  const setThinking = useGuest((s) => s.setThinking);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, thinking]);

  const send = useCallback(
    async (question: string) => {
      if (thinking) return;

      append({
        id: newGuestMessageId(),
        role: "user",
        content: question,
        createdAt: Date.now(),
      });
      setThinking(true);

      try {
        const res = await api.query({ question }, { auth: false });
        append({
          id: newGuestMessageId(),
          role: "assistant",
          content: res.answer,
          sources: res.sources ?? [],
          latencyMs: res.latency_ms,
          createdAt: Date.now(),
        });
      } catch (e) {
        const msg =
          e instanceof ApiError && e.status === 401
            ? "Guest access is disabled. Please sign in."
            : e instanceof ApiError && e.status === 429
              ? "You're sending questions too quickly. Please wait a moment."
              : e instanceof Error
                ? e.message
                : "Something went wrong. Please try again.";
        append({
          id: newGuestMessageId(),
          role: "assistant",
          content: `[!] ${msg}`,
          createdAt: Date.now(),
        });
      } finally {
        setThinking(false);
      }
    },
    [append, setThinking, thinking],
  );

  const pendingState = location.state as { pendingQuestion?: string } | null;
  const handledRef = useRef(false);
  useEffect(() => {
    if (handledRef.current) return;
    if (!pendingState?.pendingQuestion) return;
    handledRef.current = true;
    const question = pendingState.pendingQuestion;
    navigate(location.pathname, { replace: true, state: {} });
    void send(question);
  }, [pendingState, location.pathname, navigate, send]);

  const signUp = () => navigate("/register");

  return (
    <div className="sa-page sa-chat-page sa-chat-page--guest">
      <GuestBanner onSignUp={signUp} />
      <header className="sa-chat-guest-header">
        <Wordmark onClick={() => navigate("/")} />
        <div className="sa-chat-guest-header__actions">
          <ThemeToggle />
          <a onClick={() => navigate("/about")} style={{ cursor: "pointer", color: "var(--sa-fg)" }}>
            About
          </a>
          <a onClick={signUp} style={{ cursor: "pointer", color: "var(--sa-accent)" }}>
            Create account
          </a>
        </div>
      </header>

      <div
        ref={scrollRef}
        className="sa-chat-scroll"
        style={{ flex: 1, overflowY: "auto", background: "var(--sa-bg)" }}
      >
        <div className="sa-chat-thread">
          {messages.length === 0 && !thinking && (
            <div
              className="sa-chat-empty-state"
              style={{
                padding: "80px 0",
                textAlign: "center",
                fontFamily: "var(--sa-sans)",
                fontSize: 13,
                color: "var(--sa-muted-fg)",
              }}
            >
              Pose a question to begin.
            </div>
          )}
          {messages.map((message) =>
            message.role === "user" ? (
              <UserMessage
                key={message.id}
                content={message.content}
                time={formatTime(message.createdAt)}
              />
            ) : (
              <AssistantMessage
                key={message.id}
                answer={message.content}
                sources={message.sources}
                latencyMs={message.latencyMs}
                time={formatTime(message.createdAt)}
              />
            ),
          )}
          {thinking && <QueryLoading />}
          <div style={{ height: 24 }} />
        </div>
      </div>
      <Composer onSend={send} disabled={thinking} autoFocus />
    </div>
  );
}

function formatTime(ms: number): string {
  return new Date(ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}
